<!--
  - SPDX-FileCopyrightText: 2021 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<AppNavigation>
		<template #icon>
			<IconLoading v-if="loading" :size="20" />
		</template>

		<template #search>
			<header class="header">
				<slot />
			</header>
		</template>

		<!-- groups list -->
		<template #list>
			<!-- All contacts group -->
			<AppNavigationItem
				id="everyone"
				:name="GROUP_ALL_CONTACTS"
				:to="{
					name: 'group',
					params: { selectedGroup: GROUP_ALL_CONTACTS },
				}"
				:active="routeState === 'all'">
				<template #icon>
					<IconContactFilled v-if="routeState === 'all'" :size="20" />
					<IconContact v-else :size="20" />
				</template>
				<template #counter>
					<NcCounterBubble
						v-if="sortedContacts.length"
						:count="sortedContacts.length" />
				</template>
			</AppNavigationItem>

			<!-- Organization chart -->
			<AppNavigationItem
				v-if="existChart"
				id="chart"
				:name="CHART_ALL_CONTACTS"
				:to="{
					name: 'chart',
					params: { selectedChart: GROUP_ALL_CONTACTS },
				}"
				:active="routeState === 'orgchart'"
				icon="icon-category-monitoring" />

			<!-- Not grouped group -->
			<AppNavigationItem
				v-if="ungroupedContacts.length > 0"
				id="notgrouped"
				:name="GROUP_NO_GROUP_CONTACTS"
				:to="{
					name: 'group',
					params: { selectedGroup: GROUP_NO_GROUP_CONTACTS },
				}"
				:active="routeState === 'notgrouped'">
				<template #icon>
					<IconUserFilled v-if="routeState === 'notgrouped'" :size="20" />
					<IconUser v-else :size="20" />
				</template>
				<template #counter>
					<NcCounterBubble
						v-if="ungroupedContacts.length"
						:count="ungroupedContacts.length" />
				</template>
			</AppNavigationItem>

			<!-- Address books -->
			<AppNavigationCaption :name="t('contacts', 'Address books')">
				<template #actions>
					<NcActionButton @click="$refs.newAddressbook.openModal()">
						<template #icon>
							<IconAdd :size="20" />
						</template>
						{{ t('contacts', 'Add address book') }}
					</NcActionButton>
				</template>
			</AppNavigationCaption>
			<AppNavigationItem
				v-for="addressbook in enabledAddressbooks"
				:key="addressbook.id"
				:name="addressbook.displayName"
				:to="{
					name: ROUTE_ADDRESSBOOK,
					params: { selectedAddressbook: addressbook.id },
				}"
				:active="routeState === `${ROUTE_ADDRESSBOOK}:${addressbook.id}`">
				<template #icon>
					<IconAddressBook :size="20" />
				</template>
				<template #counter>
					<NcCounterBubble
						v-if="addressbookContactCount(addressbook)"
						:count="addressbookContactCount(addressbook)" />
				</template>
			</AppNavigationItem>

			<!-- Recently contacted group -->
			<AppNavigationItem
				v-if="isContactsInteractionEnabled && recentlyContactedContacts && recentlyContactedContacts.contacts.length > 0"
				id="recentlycontacted"
				:name="t('contacts', 'Recently contacted')"
				:to="{
					name: 'group',
					params: { selectedGroup: GROUP_RECENTLY_CONTACTED },
				}"
				:active="routeState === 'recentlycontacted'">
				<template #icon>
					<IconRecentlyContacted :size="20" />
				</template>
				<template #counter>
					<NcCounterBubble
						v-if="recentlyContactedContacts.contacts.length"
						:count="recentlyContactedContacts.contacts.length" />
				</template>
			</AppNavigationItem>

			<!-- OCM invitations -->
			<AppNavigationItem
				v-if="isOcmInvitesEnabled"
				id="ocm-invites"
				:name="GROUP_ALL_OCM_INVITES"
				:to="{
					name: ROUTE_NAME_ALL_OCM_INVITES,
				}"
				:active="routeState === 'ocm-invites'">
				<template #icon>
					<IconAccountSwitchOutline :size="20" />
				</template>
				<template #counter>
					<NcCounterBubble
						v-if="pendingOcmInvitesCount"
						:count="pendingOcmInvitesCount" />
				</template>
			</AppNavigationItem>

			<AppNavigationCaption
				id="newgroup"
				v-model:menu-open="isNewGroupMenuOpen"
				:force-menu="true"
				:name="t('contacts', 'Contact groups')"
				@click.prevent.stop="toggleNewGroupMenu">
				<template #actionsTriggerIcon>
					<IconAdd :size="20" />
				</template>
				<template #actions>
					<ActionText v-show="isNewGroupMenuOpen">
						<template #icon>
							<IconError v-if="createGroupError" :size="20" />
							<IconContact v-else-if="!createGroupError" :size="20" />
						</template>
						{{ createGroupError ? createGroupError : t('contacts', 'Create a new contact group') }}
					</ActionText>
					<ActionInput
						v-show="isNewGroupMenuOpen"
						icon=""
						:placeholder="t('contacts', 'Contact group name')"
						@submit.prevent.stop="createNewGroup" />
				</template>
			</AppNavigationCaption>

			<!-- Custom groups -->
			<GroupNavigationItem
				v-for="group in ellipsisGroupsMenu"
				:key="group.key"
				:route-state="routeState"
				:group="group" />

			<template v-if="isTeamManagementEnabled">
				<!-- Toggle groups ellipsis -->
				<AppNavigationItem
					v-if="groupsMenu.length > ELLIPSIS_COUNT"
					:name="collapseGroupsTitle"
					class="app-navigation__collapse"
					icon=""
					@click="onToggleGroups" />

				<!-- New circle button caption and modal -->
				<AppNavigationCaption
					id="newcircle"
					:name="t('contacts', 'Teams')">
					<template #actions>
						<NcActionButton @click="toggleNewCircleModal">
							<template #icon>
								<IconAdd :size="20" />
							</template>
							{{ t('contacts', 'Create a new team') }}
						</NcActionButton>
					</template>
				</AppNavigationCaption>
				<NewCircleIntro
					v-if="isNewCircleModalOpen"
					:loading="createCircleLoading"
					@close="closeNewCircleIntro"
					@submit="createNewCircle" />

				<template v-if="circlesMenu.length > 0">
					<!-- Circles -->
					<CircleNavigationItem
						v-for="circle in ellipsisCirclesMenu"
						:key="circle.key"
						:circle="circle" />

					<!-- Toggle circles ellipsis -->
					<AppNavigationItem
						v-if="circlesMenu.length > ELLIPSIS_COUNT"
						:name="collapseCirclesTitle"
						class="app-navigation__collapse"
						icon=""
						@click="onToggleCircles" />
				</template>

				<li class="app-navigation__circle-desc">
					{{ CIRCLE_DESC }}
				</li>
			</template>
		</template>

		<!-- settings -->

		<template #footer>
			<div class="contacts-settings">
				<NcButton
					:aria-label="t('contacts', 'Open the contacts app settings')"
					variant="tertiary"
					:wide="true"
					@click="showContactsSettings">
					<template #icon>
						<Cog :size="20" />
					</template>
					{{ CONTACTS_SETTINGS }}
				</NcButton>
			</div>
		</template>
		<ContactsSettings v-model:open="showSettings" />
		<SettingsNewAddressbook ref="newAddressbook" hide-button />
	</AppNavigation>
</template>

<script>
import { showError } from '@nextcloud/dialogs'
import { emit } from '@nextcloud/event-bus'
import {
	NcActionInput as ActionInput,
	NcActionText as ActionText,
	NcAppNavigation as AppNavigation,
	NcAppNavigationCaption as AppNavigationCaption,
	NcAppNavigationItem as AppNavigationItem,
	NcLoadingIcon as IconLoading,
	NcActionButton,
	NcButton,
	NcCounterBubble,
} from '@nextcloud/vue'
import { mapStores } from 'pinia'
import naturalCompare from 'string-natural-compare'
import IconUserFilled from 'vue-material-design-icons/Account.vue'
import IconContactFilled from 'vue-material-design-icons/AccountMultiple.vue'
import IconContact from 'vue-material-design-icons/AccountMultipleOutline.vue'
import IconUser from 'vue-material-design-icons/AccountOutline.vue'
import IconAccountSwitchOutline from 'vue-material-design-icons/AccountSwitchOutline.vue'
import IconError from 'vue-material-design-icons/AlertCircleOutline.vue'
import IconAddressBook from 'vue-material-design-icons/BookAccountOutline.vue'
import Cog from 'vue-material-design-icons/CogOutline.vue'
import IconAdd from 'vue-material-design-icons/Plus.vue'
import NewCircleIntro from '../EntityPicker/NewCircleIntro.vue'
import IconRecentlyContacted from '../Icons/IconRecentlyContacted.vue'
import CircleNavigationItem from './CircleNavigationItem.vue'
import ContactsSettings from './ContactsSettings.vue'
import GroupNavigationItem from './GroupNavigationItem.vue'
import SettingsNewAddressbook from './Settings/SettingsNewAddressbook.vue'
import RouterMixin from '../../mixins/RouterMixin.js'
import { CHART_ALL_CONTACTS, CIRCLE_DESC, CONTACTS_SETTINGS, ELLIPSIS_COUNT, GROUP_ALL_CONTACTS, GROUP_ALL_OCM_INVITES, GROUP_NO_GROUP_CONTACTS, GROUP_RECENTLY_CONTACTED, ROUTE_ADDRESSBOOK, ROUTE_NAME_ALL_OCM_INVITES } from '../../models/constants.ts'
import isContactsInteractionEnabled from '../../services/isContactsInteractionEnabled.js'
import isOcmInvitesEnabled from '../../services/isOcmInvitesEnabled.js'
import isTeamManagementEnabled from '../../services/isTeamManagementEnabled.js'
import useOcmInvitesStore from '../../store/ocminvites.ts'
import useUserGroupStore from '../../store/userGroup.ts'

export default {
	name: 'RootNavigation',

	components: {
		ActionInput,
		ActionText,
		NcActionButton,
		AppNavigation,
		NcCounterBubble,
		AppNavigationItem,
		AppNavigationCaption,
		CircleNavigationItem,
		Cog,
		ContactsSettings,
		GroupNavigationItem,
		IconAddressBook,
		IconAccountSwitchOutline,
		IconContact,
		IconContactFilled,
		IconUser,
		IconUserFilled,
		IconAdd,
		IconError,
		IconLoading,
		IconRecentlyContacted,
		NewCircleIntro,
		NcButton,
		SettingsNewAddressbook,
	},

	mixins: [RouterMixin],

	props: {
		loading: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			CIRCLE_DESC,
			CONTACTS_SETTINGS,
			ELLIPSIS_COUNT,
			GROUP_ALL_CONTACTS,
			CHART_ALL_CONTACTS,
			GROUP_NO_GROUP_CONTACTS,
			GROUP_RECENTLY_CONTACTED,
			ROUTE_ADDRESSBOOK,
			GROUP_ALL_OCM_INVITES,
			ROUTE_NAME_ALL_OCM_INVITES,

			// create group
			isNewGroupMenuOpen: false,
			createGroupError: null,

			// create circle
			isNewCircleModalOpen: false,
			createCircleLoading: false,
			createCircleError: null,

			isTeamManagementEnabled,
			isContactsInteractionEnabled,

			collapsedGroups: true,
			collapsedCircles: true,

			showSettings: false,
			isOcmInvitesEnabled,
		}
	},

	computed: {
		// navigation entry matching the current route
		routeState() {
			if (this.selectedAddressbook) {
				return `${ROUTE_ADDRESSBOOK}:${this.selectedAddressbook}`
			}
			if (this.selectedChart) {
				return 'orgchart'
			}
			if (this.$route.meta.selectedGroup === GROUP_ALL_OCM_INVITES) {
				return 'ocm-invites'
			}
			switch (this.selectedGroup) {
				case GROUP_ALL_CONTACTS:
					return 'all'
				case GROUP_NO_GROUP_CONTACTS:
					return 'notgrouped'
				case GROUP_RECENTLY_CONTACTED:
					return 'recentlycontacted'
				default:
				// group ids are built the same way in groupsMenu
					return this.selectedGroup ? `group:${this.selectedGroup.replace(' ', '_')}` : ''
			}
		},

		// store variables
		addressbooks() {
			return this.$store.getters.getAddressbooks
		},

		enabledAddressbooks() {
			return this.addressbooks.filter((ab) => ab.enabled)
		},

		circles() {
			return this.$store.getters.getCircles
		},

		contacts() {
			const contacts = this.$store.getters.getContacts
			if (contacts.undefined) {
				contacts[this.selectedContact] = contacts.undefined
				delete contacts.undefined
			}

			return this.$store.getters.getContacts
		},

		groups() {
			return this.$store.getters.getGroups
		},

		sortedContacts() {
			return this.$store.getters.getSortedContacts
		},

		userGroups() {
			return this.userGroupStore.userGroupList
		},

		// Only invitations the recipient has not accepted yet should drive
		// the navigation badge, so it reads as a pending-action count.
		pendingOcmInvitesCount() {
			return Object.values(this.ocminvitesStore.ocmInvites)
				.filter((invite) => !invite.accepted).length
		},

		// list all the contacts that doesn't have a group
		ungroupedContacts() {
			return this.sortedContacts.filter((contact) => this.contacts[contact.key]?.groups && this.contacts[contact.key]?.groups?.length === 0)
		},

		// check if any contact has manager, if not then is no need for organization chart menu
		existChart() {
			return !!Object.keys(this.contacts).filter((key) => this.contacts[key].managersName).length
		},

		// generate groups menu from the groups store
		groupsMenu() {
			const menu = this.groups.map((group) => {
				return {
					...group,
					id: group.name.replace(' ', '_'),
					key: group.name.replace(' ', '_'),
					router: {
						name: 'group',
						params: { selectedGroup: group.name },
					},
					toString: () => group.name,
				}
			})
			menu.sort((a, b) => naturalCompare(a.toString(), b.toString(), { caseInsensitive: true }))

			// Find the Recently Contacted group, delete it from array
			const recentlyIndex = menu.findIndex((group) => group.name === GROUP_RECENTLY_CONTACTED)
			if (recentlyIndex >= 0) {
				menu.splice(recentlyIndex, 1)
			}

			return menu
		},

		ellipsisGroupsMenu() {
			// If circles is not enabled, we show everything
			if (this.isTeamManagementEnabled && this.collapsedGroups) {
				return this.groupsMenu.slice(0, ELLIPSIS_COUNT)
			}
			return this.groupsMenu
		},

		// generate circles menu from the circles store
		circlesMenu() {
			const menu = [...(this.circles || []), ...(this.userGroups || [])]

			menu.sort((a, b) => {
				// If user is member of a and b, sort by level
				if (a?.initiator?.level !== b?.initiator?.level && a?.initiator?.level && b?.initiator?.level) {
					return b.initiator.level - a.initiator.level
				}

				// If user is member of a and not b, sort a first
				if (a.initiator && !b.initiator) {
					return -1
				}

				// If user is member of b and not a, sort b first
				if (!a.initiator && b.initiator) {
					return 1
				}

				// Else we sort by name
				return naturalCompare(a.toString(), b.toString(), { caseInsensitive: true })
			})

			return menu
		},

		ellipsisCirclesMenu() {
			if (this.collapsedCircles) {
				return this.circlesMenu.slice(0, ELLIPSIS_COUNT)
			}
			return this.circlesMenu
		},

		// Recently contacted data
		recentlyContactedContacts() {
			return this.groups.find((group) => group.name === GROUP_RECENTLY_CONTACTED)
		},

		// Titles for the ellipsis toggle buttons
		collapseGroupsTitle() {
			return this.collapsedGroups
				? t('contacts', 'Show all groups')
				: t('contacts', 'Collapse groups')
		},

		collapseCirclesTitle() {
			return this.collapsedCircles
				? t('contacts', 'Show all teams')
				: t('contacts', 'Collapse teams')
		},

		...mapStores(useOcmInvitesStore, useUserGroupStore),
	},

	methods: {
		addressbookContactCount(addressbook) {
			// contact groups are stored as contacts too, but never listed as such
			return Object.values(addressbook.contacts || {}).filter((contact) => contact.kind !== 'group').length
		},

		toggleNewGroupMenu() {
			this.isNewGroupMenuOpen = !this.isNewGroupMenuOpen
		},

		createNewGroup(e) {
			const input = e.target.querySelector('input[type=text]')
			const groupName = input.value.trim()
			if (groupName.length === 0) {
				showError(t('contacts', 'Group name cannot be empty'))
				return
			}
			this.logger.debug('Creating new group', { groupName })

			// Check if already exists
			if (this.groups.find((group) => group.name === groupName)) {
				this.createGroupError = t('contacts', 'This group already exists')
				emit('contacts:group:append', this.groups.find((group) => group.name === groupName).name)
				return
			}

			this.createGroupError = null
			this.logger.debug('Created new local group', { groupName })

			try {
				this.$store.dispatch('addGroup', groupName)
				this.isNewGroupMenuOpen = false
				emit('contacts:group:append', groupName)
			} catch (error) {
				showError(t('contacts', 'An error occurred while creating the group'))
			}
		},

		// Ellipsis item toggles
		onToggleGroups() {
			this.collapsedGroups = !this.collapsedGroups
		},

		onToggleCircles() {
			this.collapsedCircles = !this.collapsedCircles
		},

		toggleNewCircleModal() {
			this.isNewCircleModalOpen = true
		},

		async createNewCircle(circleName, isPersonal, isLocal) {
			this.logger.debug('Creating new team', { circleName })

			this.createCircleLoading = true

			// Check if already exists
			if (this.circles.find((circle) => circle.name === circleName)) {
				this.createCircleError = t('contacts', 'This team already exists')
				return
			}
			this.createCircleError = null

			try {
				const circle = await this.$store.dispatch('createCircle', { circleName, isPersonal, isLocal })
				this.closeNewCircleIntro()

				// Select group
				this.$router.push({
					name: 'circle',
					params: {
						selectedCircle: circle.id,
					},
				})
			} catch (error) {
				showError(t('contacts', 'An error happened during the creation of the team'))
			} finally {
				this.createCircleLoading = false
			}
		},

		closeNewCircleIntro() {
			this.isNewCircleModalOpen = false
		},

		/**
		 * Shows the contacts settings
		 */
		showContactsSettings() {
			this.showSettings = true
		},

	},
}
</script>

<style lang="scss" scoped>
$caption-padding: 22px;

.header {
	padding: calc(var(--default-grid-baseline, 4px) * 2);
}

#external-invitations,
#newgroup,
#newcircle {
	margin-top: $caption-padding;

	:deep(a) {
		color: var(--color-text-maxcontrast)
	}
}

.app-navigation__circle-desc {
	margin: var(--default-grid-baseline, 4px) calc(var(--default-grid-baseline, 4px) * 2);
	color: var(--color-text-maxcontrast);
}

.app-navigation__collapse :deep(a) {
	color: var(--color-text-maxcontrast)
}

:deep(.settings-button__label) {
	opacity: .7;
	font-weight: bold;
}

.contacts-settings {
	padding: calc(var(--default-grid-baseline, 4px) * 2);

	:deep(.button-vue__wrapper) {
		justify-content: flex-start !important;
	}
}

.contacts-settings-button {
	width: 100%;
	justify-content: start !important;
}

</style>
